//
// Copyright 2022 DXOS.org
//

import * as Predicate from 'effect/Predicate';

import { Event, asyncTimeout } from '@dxos/async';
import { Context } from '@dxos/context';
import { type Obj, Query, type QueryResult } from '@dxos/echo';
import { filterMatchDoc } from '@dxos/echo-host/filter';
import { QueryPlanner } from '@dxos/echo-host/query';
import { QueryAST } from '@dxos/echo-protocol';
import { type EntityId, type SpaceId } from '@dxos/keys';
import { log } from '@dxos/log';

import { type ItemsUpdatedEvent, type ObjectCore } from '../core-db';
import { prohibitSignalActions } from '../guarded-scope';
import { type DatabaseImpl } from '../proxy-db';
import { type QueryContext } from './query-context';
import { getTargetSpacesForQuery, isSimpleSelectionQuery } from './util';
import { type WorkingSetDataProvider, type WorkingSetItem, WorkingSetQueryExecutor } from './working-set-executor';

export type GraphQueryContextProps = {
  // TODO(dmaretskyi): Make async.
  onStart: () => void;
  onStop: () => void;
};

/**
 * Query data source.
 * Implemented by a space or a remote agent.
 * Each query has a separate instance.
 */
export interface QuerySource {
  // TODO(dmaretskyi): Update info?
  changed: Event<void>;

  // TODO(dmaretskyi): Make async.
  open(): void;

  // TODO(dmaretskyi): Make async.
  close(): void;

  /**
   * Synchronous query.
   */
  getResults(): QueryResult.EntityEntry[];

  /**
   * One-shot query.
   */
  run(ctx: Context, query: QueryAST.Query): Promise<QueryResult.EntityEntry[]>;

  /**
   * Set the filter and trigger continuous updates.
   */
  update(query: QueryAST.Query): void;
}

/**
 * Aggregates multiple query sources.
 */
export class GraphQueryContext implements QueryContext {
  private readonly _sources = new Set<QuerySource>();

  private _query?: QueryAST.Query = undefined;

  private _ctx?: Context = undefined;

  public changed = new Event<void>();

  constructor(private readonly _params: GraphQueryContextProps) {}

  get sources(): ReadonlySet<QuerySource> {
    return this._sources;
  }

  start() {
    this._ctx = new Context();
    this._params.onStart();
    for (const source of this._sources) {
      source.open();
      if (this._query) {
        source.update(this._query);
      }

      // Subscribing after `update` means that we will intentionally skip any `changed` events generated by update.
      source.changed.on(this._ctx, () => {
        this.changed.emit();
      });
    }
  }

  stop() {
    void this._ctx?.dispose();
    for (const source of this.sources) {
      source.close();
    }
    this._params.onStop();
  }

  getResults(): QueryResult.EntityEntry[] {
    // TODO: dedup by meta.key based on scope order (space results take precedence over registry).
    if (!this._query) {
      return [];
    }
    return Array.from(this._sources).flatMap((source) => source.getResults());
  }

  async run(
    ctx: Context,
    query: QueryAST.Query,
    { timeout = 30_000 }: QueryResult.RunOptions = {},
  ): Promise<QueryResult.EntityEntry[]> {
    const runTasks = [...this._sources.values()].map(async (s) => {
      try {
        log('run query', {
          resolver: Object.getPrototypeOf(s).constructor.name,
          query: Query.pretty(Query.fromAst(query)),
        });
        const results = await asyncTimeout<QueryResult.EntityEntry[]>(s.run(ctx, query), timeout);
        log('run query results', {
          resolver: Object.getPrototypeOf(s).constructor.name,
          count: results.length,
          query: Query.pretty(Query.fromAst(query)),
        });
        return results;
      } catch (err) {
        log('run query error', {
          resolver: Object.getPrototypeOf(s).constructor.name,
          query: Query.pretty(Query.fromAst(query)),
          error: err,
        });
        throw err;
      }
    });
    if (runTasks.length === 0) {
      return [];
    }
    const mergedResults = (await Promise.all(runTasks)).flatMap((r) => r ?? []);
    return mergedResults;
  }

  update(query: QueryAST.Query): void {
    this._query = query;
    for (const source of this._sources) {
      source.update(query);
    }
  }

  addQuerySource(querySource: QuerySource) {
    this._sources.add(querySource);
    if (this._ctx != null) {
      querySource.changed.on(this._ctx, () => {
        this.changed.emit();
      });
    }
    if (this._query) {
      querySource.update(this._query);
    }
  }
}

/**
 * Queries objects from the local working set.
 */
export class SpaceQuerySource implements QuerySource {
  public readonly changed = new Event<void>();

  private _ctx: Context = new Context();
  private _query: QueryAST.Query | undefined = undefined;
  private _results?: QueryResult.EntityEntry<Obj.Any>[] = undefined;
  private readonly _executor: WorkingSetQueryExecutor;
  private readonly _planner: QueryPlanner;

  constructor(private readonly _database: DatabaseImpl) {
    const provider: WorkingSetDataProvider = {
      get spaceId(): SpaceId {
        return _database.spaceId;
      },
      allCores: () => _database.allObjectCores(),
      getCoreById: (id, load) => _database.getObjectCoreById(id, { load: load ?? false }),
      areStrongDepsSatisfied: (core) => _database.areStrongDepsSatisfied(core),
    };
    this._executor = new WorkingSetQueryExecutor(provider);
    this._planner = new QueryPlanner({ defaultTextSearchKind: 'full-text', noIndexes: true });
  }

  get spaceId() {
    return this._database.spaceId;
  }

  get spaceKey() {
    return this._database.spaceKey;
  }

  open(): void {}

  close() {
    this._results = undefined;
    void this._ctx.dispose().catch(() => {});
  }

  private _onUpdate = (updateEvent: ItemsUpdatedEvent) => {
    if (!this._query) {
      return;
    }

    prohibitSignalActions(() => {
      // Results haven't been computed yet — no stale cache to invalidate.
      if (!this._results) {
        return;
      }

      // TODO(dmaretskyi): Could be optimized to recompute changed only to the relevant space.
      const changed = updateEvent.itemsUpdated.some(({ id: objectId }) => {
        // If any updated object was in previous results, invalidate.
        if (this._results!.find((result) => result.id === objectId)) {
          return true;
        }

        // For simple queries, use the lightweight filter check for the newly-updated object.
        const trivial = isSimpleSelectionQuery(this._query!);
        if (trivial && !trivial.hasQueues) {
          const core = this._database._entityManager.getObjectCoreById(objectId, { load: false });
          return core != null && this._filterCore(core, trivial.filter, trivial.options);
        }

        // For complex queries (handled by executor), conservatively invalidate on any update.
        // The next getResults() call will recompute via the executor.
        return true;
      });

      if (changed) {
        this._results = undefined;
        this.changed.emit();
      }
    });
  };

  async run(ctx: Context, query: QueryAST.Query): Promise<QueryResult.EntityEntry<Obj.Unknown>[]> {
    if (!this._isValidSourceForQuery(query)) {
      return [];
    }

    const simple = isSimpleSelectionQuery(query);

    // For queries that contain traversals, unions, or set-difference nodes — structures that the
    // SQL-backed source cannot handle — try the working-set executor. If it can satisfy the query
    // entirely from in-memory cores, return those results; otherwise return empty.
    if (requiresWorkingSetExecutor(query)) {
      const executorResults = this._tryExecuteWithWorkingSet(query);
      if (executorResults !== null) {
        return executorResults.map((item) => this._mapItemToResult(item));
      }
      return [];
    }

    // Simple path returned null for non-traversal reasons (limit, order, etc.) — the SQL-backed
    // source handles those entirely; this source has nothing to contribute.
    if (!simple || simple.hasQueues) {
      return [];
    }

    // Simple selection path: load by id when needed, then scan the working set.
    const { filter, options } = simple;
    const results: QueryResult.EntityEntry<Obj.Any>[] = [];
    if (isObjectIdFilter(filter)) {
      results.push(
        ...(await this._database.batchLoadObjectCores((filter as QueryAST.FilterObject).id as EntityId[]))
          .filter(Predicate.isNotUndefined)
          .filter((core) => this._filterCore(core, filter, options))
          .map((core) => this._mapCoreToResult(core)),
      );
    }

    prohibitSignalActions(() => {
      results.push(...this._queryWorkingSet(filter, options));
    });

    // Dedup
    const map = new Map<string, QueryResult.EntityEntry<Obj.Unknown>>();
    for (const result of results) {
      map.set(result.id, result);
    }

    return [...map.values()];
  }

  getResults(): QueryResult.EntityEntry<Obj.Unknown>[] {
    if (!this._query) {
      return [];
    }

    if (!this._results) {
      prohibitSignalActions(() => {
        this._results = this._computeResults(this._query!);
      });
    }

    return this._results!;
  }

  private _computeResults(query: QueryAST.Query): QueryResult.EntityEntry<Obj.Unknown>[] {
    // For queries that contain traversals, unions, or set-difference nodes, the SQL-backed source
    // cannot handle them — use the working-set executor instead.
    if (requiresWorkingSetExecutor(query)) {
      const executorResults = this._tryExecuteWithWorkingSet(query);
      if (executorResults !== null) {
        return executorResults.map((item) => this._mapItemToResult(item));
      }
      return [];
    }

    const trivial = isSimpleSelectionQuery(query);

    // Non-traversal reasons for isSimpleSelectionQuery returning null (limit, order, etc.) —
    // the SQL-backed source handles those; this source has nothing to contribute.
    if (!trivial || trivial.hasQueues) {
      return [];
    }

    // Simple selection path: scan the working set directly.
    const { filter, options } = trivial;
    return this._queryWorkingSet(filter, options);
  }

  update(query: QueryAST.Query): void {
    if (!this._isValidSourceForQuery(query)) {
      this._query = undefined;
      return;
    }

    void this._ctx.dispose().catch(() => {});
    this._ctx = new Context();
    this._query = query;

    this._database._updateEvent.on(this._ctx, this._onUpdate);

    this._results = undefined;
    this.changed.emit();
  }

  /**
   * Queries from already loaded objects.
   */
  private _queryWorkingSet(
    filter: QueryAST.Filter,
    options: QueryAST.QueryOptions | undefined,
  ): QueryResult.EntityEntry<Obj.Unknown>[] {
    const filteredCores = isObjectIdFilter(filter)
      ? (filter as QueryAST.FilterObject)
          .id!.map((id) => this._database.getObjectCoreById(id, { load: true }))
          .filter(Predicate.isNotUndefined)
          .filter((core) => this._filterCore(core, filter, options))
      : this._database.allObjectCores().filter((core) => this._filterCore(core, filter, options));

    return filteredCores.map((core) => this._mapCoreToResult(core));
  }

  /**
   * Attempt to resolve a query entirely from the in-memory working set.
   * Returns null when the plan requires SQL index capabilities.
   */
  private _tryExecuteWithWorkingSet(query: QueryAST.Query): WorkingSetItem[] | null {
    try {
      const plan = this._planner.createPlan(query);
      return this._executor.tryExecute(plan);
    } catch {
      // Query planner threw (e.g. unsupported query shape). Fall back.
      return null;
    }
  }

  private _mapItemToResult(item: WorkingSetItem): QueryResult.EntityEntry<Obj.Unknown> {
    return {
      id: item.objectId,
      result: this._database.getObjectById(item.objectId, { deleted: true }),
      match: { rank: 1 },
      resolution: {
        source: 'local',
        time: 0,
      },
    };
  }

  private _isValidSourceForQuery(query: QueryAST.Query): boolean {
    const targetSpaces = getTargetSpacesForQuery(query);
    // Disabled by spaces filter.
    if (targetSpaces.length > 0 && !targetSpaces.includes(this.spaceId)) {
      return false;
    }

    // Disabled if the from clause has explicit scopes but none target spaces (e.g. registry-only).
    let hasExplicitNonEmptyScope = false;
    let hasSpaceScope = false;
    QueryAST.visit(query, (node) => {
      if (node.type === 'from' && node.from._tag === 'scope' && node.from.scopes.length > 0) {
        hasExplicitNonEmptyScope = true;
        if (node.from.scopes.some((s) => s._tag === 'space')) {
          hasSpaceScope = true;
        }
      }
    });
    if (hasExplicitNonEmptyScope && !hasSpaceScope) {
      return false;
    }

    return true;
  }

  private _mapCoreToResult(core: ObjectCore): QueryResult.EntityEntry<Obj.Unknown> {
    return {
      id: core.id,
      result: this._database.getObjectById(core.id, { deleted: true }),
      match: { rank: 1 },
      resolution: {
        source: 'local',
        time: 0,
      },
    };
  }

  private _filterCore(core: ObjectCore, filter: QueryAST.Filter, options: QueryAST.QueryOptions | undefined): boolean {
    return (
      this._database.areStrongDepsSatisfied(core) &&
      filterCoreByDeletedFlag(core, options) &&
      filterMatchDoc(filter, {
        id: core.id,
        doc: core.getObjectStructure(),
        spaceId: this.spaceId,
      })
    );
  }
}

const isObjectIdFilter = (filter: QueryAST.Filter) => {
  return filter.type === 'object' && filter.id !== undefined && filter.id.length > 0;
};

const filterCoreByDeletedFlag = (core: ObjectCore, options: QueryAST.QueryOptions | undefined): boolean => {
  switch (options?.deleted) {
    case undefined:
    case 'exclude':
      return !core.isDeleted();
    case 'include':
      return true;
    case 'only':
      return core.isDeleted();
  }
};

/**
 * Query node types whose shapes the SQL-backed source does not satisfy locally; the working-set
 * executor handles them. Plain select/filter/limit/order queries stay on the SQL path.
 */
const WORKING_SET_NODE_TYPES = new Set<QueryAST.Query['type']>([
  'reference-traversal',
  'relation-traversal',
  'hierarchy-traversal',
  'incoming-references',
  'relation',
  'union',
  'set-difference',
]);

/**
 * Returns true when the query contains traversal, relation, union, set-difference, or child-of
 * filter nodes. These query types cannot be satisfied by the SQL index and require the working-set
 * executor. Limit and order nodes alone do not qualify — those are handled entirely by the SQL source.
 * Child-of filters have no traversal AST node — they appear as filter predicates — so they must
 * be detected by visiting the filter tree directly.
 */
const requiresWorkingSetExecutor = (query: QueryAST.Query): boolean => {
  let found = false;
  QueryAST.visit(query, (node) => {
    if (WORKING_SET_NODE_TYPES.has(node.type)) {
      found = true;
    }
    if ((node.type === 'filter' || node.type === 'select') && _filterContainsChildOf(node.filter)) {
      found = true;
    }
  });
  return found;
};

/**
 * Returns true if the filter is or composes a child-of predicate.
 * Child-of filters require in-memory parent-chain traversal which the SQL index cannot handle.
 */
const _filterContainsChildOf = (filter: QueryAST.Filter): boolean => {
  switch (filter.type) {
    case 'child-of':
      return true;
    case 'not':
      return _filterContainsChildOf(filter.filter);
    case 'and':
    case 'or':
      return filter.filters.some(_filterContainsChildOf);
    default:
      return false;
  }
};
